import {isSketchArcObject, isSketchCircleObject} from '../../../mesh/guards';
import type {ISketchObject} from '../../../mesh/sketch/ISketchObject';
import type {
    SketchEntityParameterAdapter,
} from '../../../../types/sketch/solver/SketchParametricRuntimeTypes';
import {SKETCH_PARAMETRIC_TOLERANCE} from '../core/SketchParametricToleranceConfig';

/**
 * 圆参数适配器。
 *
 * 圆的半径是**权威字段**、边控制点是它的派生（与圆弧相反），
 * 且圆未 override `refreshGeometryOnly`，所以挪点不会改半径——必须显式写半径。
 */
const circleAdapter: SketchEntityParameterAdapter = {
    readParameters: (entity) => (isSketchCircleObject(entity) ? {radius: entity.radius} : {}),
    applyParameters: (entity, parameters) => {
        if (!isSketchCircleObject(entity) || parameters.radius === undefined) {
            return false;
        }
        if (!Number.isFinite(parameters.radius)
            || parameters.radius <= SKETCH_PARAMETRIC_TOLERANCE.degenerateLength) {
            return false;
        }
        if (Math.abs(entity.radius - parameters.radius) <= SKETCH_PARAMETRIC_TOLERANCE.degenerateLength) {
            return false;
        }
        // 静默写回：同步半径并联动边控制点，不触发对象级派生通知。
        entity.setRadiusSilently(parameters.radius);

        return true;
    },
};

/**
 * 圆弧参数适配器。
 *
 * 端点/圆心由参数解负责（`pointOnCircle` 结构方程保证端点已落在新圆周上），
 * 适配器只把半径写进 arcDefinition，并**按当前控制点反算起止角**——绝不反过来
 * 按定义重摆控制点。曾经 `syncControlPointsFromDefinition`（旧角度 × 新半径）
 * 把解好的共享端点拽走 20mm+：与端点相连的其他形状被带歪，且运行时终态偏离
 * solver island 快照 —— Redo 会重放出第三种形状。
 */
const arcAdapter: SketchEntityParameterAdapter = {
    readParameters: (entity) => (isSketchArcObject(entity) ? {radius: entity.getArcDefinition().radius} : {}),
    applyParameters: (entity, parameters) => {
        if (!isSketchArcObject(entity) || parameters.radius === undefined) {
            return false;
        }
        if (!Number.isFinite(parameters.radius)
            || parameters.radius <= SKETCH_PARAMETRIC_TOLERANCE.degenerateLength) {
            return false;
        }

        const definition = entity.getArcDefinition();

        if (Math.abs(definition.radius - parameters.radius) <= SKETCH_PARAMETRIC_TOLERANCE.degenerateLength) {
            return false;
        }

        const referencePlane = entity.referencePlane;
        const centerWorld = entity.getCenterPoint().getPosition();
        const centerLocal = referencePlane.worldToLocal(centerWorld);
        const startLocal = referencePlane.worldToLocal(entity.getStartPoint().getPosition());
        const endLocal = referencePlane.worldToLocal(entity.getEndPoint().getPosition());
        const startAngle = Math.atan2(startLocal.y - centerLocal.y, startLocal.x - centerLocal.x);
        let endAngle = Math.atan2(endLocal.y - centerLocal.y, endLocal.x - centerLocal.x);

        // 与 rebuildDefinitionFromControlPoints 同款走向规约，保持原扫向。
        if (definition.clockwise) {
            if (endAngle > startAngle) {
                endAngle -= Math.PI * 2;
            }
        } else if (endAngle < startAngle) {
            endAngle += Math.PI * 2;
        }

        entity.applySolvedArcDefinition({
            ...definition,
            centerWorld: centerWorld.clone(),
            radius: parameters.radius,
            startAngle,
            endAngle,
        });

        return true;
    },
};

/**
 * entityKind（小写归一）到实体参数适配器。
 *
 * 参数求解的自由度并非全是点；每种实体在本适配层声明运行时读写方式，
 * 求解核心只处理 plain data。所有适配器必须使用静默写回通道，禁止在写回阶段再次启动求解。
 */
const adapterByEntityKind: ReadonlyMap<string, SketchEntityParameterAdapter> = new Map([
    ['circle', circleAdapter],
    ['arc', arcAdapter],
]);

/**
 * 解析实体参数写回适配器。
 *
 * @param entity 运行时实体。
 * @returns 适配器；该实体类型无非点自由度时返回 null。
 */
export function resolveSketchEntityParameterAdapter(entity: ISketchObject): SketchEntityParameterAdapter | null {
    const entityKind = String(Reflect.get(entity, 'objectType') ?? '').toLowerCase();

    return adapterByEntityKind.get(entityKind) ?? null;
}

/**
 * 从实体几何快照解析半径自由度。
 *
 * 说明：「某类实体的半径存在哪」是参数化契约的一部分，必须由**声明侧（island 构建器
 * 注册自由参数）与写回侧共用同一份知识**，否则会出现"注册不了参数 → 尺寸判为未支持"
 * 这类静默缺口：圆的半径在 `radius` 字段上，圆弧的半径却在 `arcDefinition.radius` 里，
 * 只读 `radius` 会让圆弧永远拿不到半径参数、半径尺寸整体不可用。
 *
 * @param entityKind 实体类型（大小写不敏感）。
 * @param geometry 实体几何快照。
 * @returns 半径；该实体类型无半径自由度或快照缺失时返回 null。
 */
export function resolveEntityRadiusFromGeometrySnapshot(
    entityKind: string,
    geometry: Readonly<Record<string, unknown>>,
): number | null {
    const normalizedKind = entityKind.toLowerCase();

    if (normalizedKind !== 'circle' && normalizedKind !== 'arc') {
        return null;
    }

    const directRadius = geometry.radius;

    if (typeof directRadius === 'number' && Number.isFinite(directRadius)) {
        return directRadius;
    }

    const arcDefinition = geometry.arcDefinition;
    const arcRadius = arcDefinition && typeof arcDefinition === 'object'
        ? Reflect.get(arcDefinition, 'radius')
        : undefined;

    return typeof arcRadius === 'number' && Number.isFinite(arcRadius) ? arcRadius : null;
}
